import { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { useAdmin } from './AdminContext';
import { REALMS } from '../../../../shared/src/realms';

interface QuestionAccuracy {
  id: string; realm: string; difficulty: string; question_text: string;
  times_served: number; times_correct: number; accuracy: number;
}

export function MatchAnalytics() {
  const { toast } = useAdmin();
  const [tab, setTab] = useState<'questions' | 'matches'>('questions');
  const [questions, setQuestions] = useState<QuestionAccuracy[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    if (tab === 'questions') fetchQuestionStats();
  }, [tab]);

  const fetchQuestionStats = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_questions', {
      p_min_served: 3,
      p_order: 'served_desc',
      p_limit: 200,
    });

    if (error) { toast(error.message, 'error'); setLoading(false); return; }

    const processed = ((data || []) as Omit<QuestionAccuracy, 'accuracy'>[]).map(q => ({
      ...q,
      accuracy: q.times_served > 0 ? Math.round((q.times_correct / q.times_served) * 100) : 0,
    }));

    setQuestions(processed);
    setLoading(false);
  };

  const sortedQuestions = [...questions].sort((a, b) => sortAsc ? a.accuracy - b.accuracy : b.accuracy - a.accuracy);

  const updateDifficulty = async (id: string, diff: string) => {
    const { error } = await supabase.from('questions').update({ difficulty: diff }).eq('id', id);
    if (!error) {
      setQuestions(prev => prev.map(q => q.id === id ? { ...q, difficulty: diff } : q));
      toast(`Difficulty → ${diff}`, 'info');
    }
  };

  // Pull a likely-broken question out of rotation (e.g. wrong correct_index →
  // accuracy far below random chance). Sets active=false so it stops being served.
  const disableQuestion = async (id: string) => {
    const { error } = await supabase.from('questions').update({ active: false }).eq('id', id);
    if (error) { toast(error.message, 'error'); return; }
    setQuestions(prev => prev.filter(q => q.id !== id));
    toast('Question disabled — removed from rotation', 'info');
  };

  return (
    <div>
      <h2 className="admin-section-title" style={{ marginBottom: '20px' }}>ANALYTICS</h2>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: '#0a0a0a', borderRadius: '8px', padding: '3px', maxWidth: '300px' }}>
        {(['questions', 'matches'] as const).map(t => (
          <button key={t} className="admin-btn" onClick={() => setTab(t)}
            style={{
              flex: 1, background: tab === t ? '#c4973a' : 'transparent',
              color: tab === t ? '#000' : '#bebec6', fontSize: '0.85rem',
            }}>
            {t === 'questions' ? 'QUESTION PERF' : 'MATCH HISTORY'}
          </button>
        ))}
      </div>

      {tab === 'questions' && (
        <div className="admin-card" style={{ padding: 0 }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #243450', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: '#ccccd4', fontFamily: 'var(--font-mono)' }}>
              {questions.length} questions with ≥3 serves
            </span>
            <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setSortAsc(!sortAsc)}>
              Sort: {sortAsc ? 'Worst first ↑' : 'Best first ↓'}
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>REALM</th>
                  <th style={{ width: '60px' }}>DIFF</th>
                  <th>QUESTION</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>ACCURACY</th>
                  <th style={{ width: '60px', textAlign: 'center' }}>SERVED</th>
                  <th style={{ width: '80px' }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#808080' }}>Loading...</td></tr>
                ) : sortedQuestions.map(q => {
                  const accColor = q.accuracy < 20 ? '#ef4444' : q.accuracy < 40 ? '#f59e0b' : q.accuracy > 90 ? '#6366f1' : '#10b981';
                  return (
                    <tr key={q.id}>
                      <td style={{ color: REALMS[q.realm as keyof typeof REALMS]?.color || '#fff', fontSize: '0.85rem', fontWeight: 'bold' }}>{q.realm}</td>
                      <td><span className={`admin-badge admin-badge-${q.difficulty}`}>{q.difficulty.slice(0, 3).toUpperCase()}</span></td>
                      <td style={{ color: '#dcdce0', fontSize: '0.82rem', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {q.question_text}
                      </td>
                      <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.8rem', fontWeight: 'bold', color: accColor }}>
                        {q.accuracy}%
                      </td>
                      <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: '#808080' }}>{q.times_served}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {q.accuracy < 20 && q.difficulty !== 'hard' && (
                            <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => updateDifficulty(q.id, 'hard')}>→ HARD</button>
                          )}
                          {q.accuracy > 90 && q.difficulty !== 'easy' && (
                            <button className="admin-btn admin-btn-success admin-btn-sm" onClick={() => updateDifficulty(q.id, 'easy')}>→ EASY</button>
                          )}
                          {q.accuracy < 20 && q.times_served >= 15 && (
                            <button
                              className="admin-btn admin-btn-danger admin-btn-sm"
                              title="Accuracy far below random chance — the correct answer is likely wrong. Pull it from rotation."
                              onClick={() => disableQuestion(q.id)}
                            >
                              ✕ SUSPECT
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'matches' && (
        <div className="admin-card" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📈</div>
          <h3 style={{ fontFamily: 'var(--font-heading)', color: '#818cf8', margin: '0 0 8px 0' }}>COMING SOON</h3>
          <p style={{ color: '#808080', fontSize: '0.85rem' }}>
            Match history analytics will use a Postgres RPC for aggregated player and match data.
          </p>
        </div>
      )}
    </div>
  );
}
